import { useState, useEffect } from 'react';
import PlayerList from './components/PlayerList';
import CourtView from './components/CourtView';
import PlayerKanban from './components/PlayerKanban';
import Statistics from './components/Statistics';
import Modal from './components/Modal';
import CourtManagement from './components/CourtManagement';
import HistoryPage from './components/HistoryPage';
import ManualScheduler from './components/ManualScheduler';
import DebugView from './components/DebugView'; // Import DebugView
import { CssBaseline, Container, Box, Typography, Button, Stack, CircularProgress, Alert, Switch, FormControlLabel } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { NotificationProvider } from './context/NotificationContext';
import { pingBackend, refillBuffer } from './services/api';

// Custom theme remains the same
const customTheme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: '8px', textTransform: 'none' },
        contained: { backgroundColor: '#1a1a1a', color: 'white', '&:hover': { backgroundColor: '#333' } },
        outlined: { borderColor: '#1a1a1a', color: '#1a1a1a', '&:hover': { backgroundColor: 'rgba(26, 26, 26, 0.04)', borderColor: '#1a1a1a' } },
      },
    },
  },
});

function App() {
  const [backendStatus, setBackendStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [isDebugMode, setDebugMode] = useState(false); // State for debug mode
  
  // App-specific states
  const [isPlayerModalOpen, setPlayerModalOpen] = useState(false);
  const [isCourtModalOpen, setCourtModalOpen] = useState(false);
  const [isManualSchedulerOpen, setManualSchedulerOpen] = useState(false);
  const [view, setView] = useState<'main' | 'history'>('main');

  useEffect(() => {
    const checkBackend = () => {
      let attempts = 0;
      const maxAttempts = 15; // Try for 15 seconds

      const interval = setInterval(async () => {
        try {
          await pingBackend();
          setBackendStatus('ready');
          clearInterval(interval);
          // Prime the match buffer on startup
          console.log("Priming match buffer...");
          refillBuffer().then(response => console.log(response.message));
        } catch (error) {
          attempts++;
          if (attempts >= maxAttempts) {
            setBackendStatus('error');
            clearInterval(interval);
          }
        }
      }, 1000);
    };

    checkBackend();
  }, []);

  const renderMainView = () => (
    <>
      <CourtView />
      <PlayerKanban />
      <Statistics />
    </>
  );
  
  const MainApp = () => (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            智慧羽球排點系統
          </Typography>
          <FormControlLabel
            control={<Switch checked={isDebugMode} onChange={(e) => setDebugMode(e.target.checked)} />}
            label="除錯模式"
          />
        </Box>
        
        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <Button variant={view === 'main' ? "contained" : "outlined"} onClick={() => setView('main')}>儀表板</Button>
          <Button variant={view === 'history' ? "contained" : "outlined"} onClick={() => setView('history')}>歷史對戰</Button>
        </Stack>
        
        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <Button variant="outlined" onClick={() => setPlayerModalOpen(true)}>球員管理</Button>
          <Button variant="outlined" onClick={() => setCourtModalOpen(true)}>場地管理</Button>
          <Button variant="outlined" onClick={() => setManualSchedulerOpen(true)}>手動排點</Button>
        </Stack>

        <main>
          {view === 'main' ? renderMainView() : <HistoryPage onBack={() => setView('main')} />}
        </main>
        
        {isDebugMode && <DebugView />}

      </Box>
      <Modal isOpen={isPlayerModalOpen} onClose={() => setPlayerModalOpen(false)} title="球員資料庫">
        <PlayerList />
      </Modal>

      <Modal isOpen={isCourtModalOpen} onClose={() => setCourtModalOpen(false)} title="場地管理">
        <CourtManagement />
      </Modal>

      <Modal isOpen={isManualSchedulerOpen} onClose={() => setManualSchedulerOpen(false)} title="手動排點">
        <ManualScheduler onClose={() => setManualSchedulerOpen(false)} />
      </Modal>
    </Container>
  );

  return (
    <ThemeProvider theme={customTheme}>
      <NotificationProvider>
        <CssBaseline />
        {backendStatus === 'loading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
            <CircularProgress />
            <Typography>正在與後端服務連線中，請稍候...</Typography>
          </Box>
        )}
        {backendStatus === 'error' && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            <Alert severity="error">無法連線至後端服務，請確認後端是否正常運作或重新整理頁面。</Alert>
          </Box>
        )}
        {backendStatus === 'ready' && <MainApp />}
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
